import React, { FC, useEffect, SyntheticEvent, memo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Modal as MuiModal } from '@material-ui/core';
import Backdrop from '@material-ui/core/Backdrop';
import cn from 'classnames';
import { useHistory } from 'react-router';
import { History } from 'history';
import InfoView from 'src/components/InfoView';
import SpinnerOverlay from 'src/components/SpinnerOverlay';
import { ITheme } from 'src/App';
import { ModalInterface, IFooterClasses } from './interfaces';
import ModalHeader, { IHeaderClasses } from './ModalHeader';
import ModalActions, { FOOTER_KEY } from './ModalActions';

interface IModalClasses extends IHeaderClasses, IFooterClasses{
  modal?: string,
  paper?: string,
  content?: string,
  body?: string,
}

export const BODY_KEY = 'modal-body-container';
export { FOOTER_KEY };

const useStyles = makeStyles(
  ({
    palette,
    shadows,
    spacing,
  }: ITheme) => ({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    paper: {
      display: 'flex',
      backgroundColor: palette.typical.white,
      boxShadow: shadows[5],
      borderRadius: spacing(0.5),
      overflow: 'hidden',
      outline: 0,
      minWidth: '60% !important',
      maxWidth: '70%',
      maxHeight: '85%',
      minHeight: '10rem',
    },
    content: {
      display: 'flex',
      width: '100%',
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'space-between',
      overflow: 'hidden',
    },
    body: {
      overflowY: 'auto',
      margin: spacing(1, 0, 0.2, 0),
      padding: spacing(0, 2),
    },
  }),
);

const getHeaderClasses = (classes: IModalClasses): IHeaderClasses => {
  const { header, title, closeButton } = classes;
  return { header, title, closeButton };
};

const getFooterClasses = (classes: IModalClasses): IFooterClasses => {
  const { footer, actions, buttonPrimary, buttonSecondary, cancelButton } = classes;
  return { footer, actions, buttonPrimary, buttonSecondary, cancelButton };
};

const Modal: FC<ModalInterface> = ({
  open = true,
  title,
  actions = [],
  parentUrl,
  onCloseEffect,
  onMountEffect,
  children,
  classes: overrideClasses = {},
  showFooter = true,
  showCancel = true,
  isLoading = false,
  error,
  ...rest
}) => {
  const history: History = useHistory();

  useEffect(() => {
    if (onMountEffect) {
      onMountEffect();
    }
  }, []);

  const handleCloseModal = (event: SyntheticEvent<{}, Event>) => {
    event.stopPropagation();
    if (parentUrl) {
      history.push(parentUrl);
    }

    if (onCloseEffect) {
      onCloseEffect();
    }
  };

  const classes: IModalClasses = useStyles({ classes: overrideClasses });

  return (
    <MuiModal
      disableAutoFocus
      className={classes.modal}
      open={open}
      onClose={handleCloseModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      disableBackdropClick
      {...rest}
    >
      <div className={classes.paper}>
        <SpinnerOverlay showOverlay={isLoading && !error}>
          <div className={classes.content}>
            {title && (
              <ModalHeader
                title={title}
                classes={getHeaderClasses(classes)}
                onClose={handleCloseModal}
              />
            )}
            <div className={cn(classes.body, BODY_KEY)}>{error ? <InfoView seleniumId="modal-error" title={error} /> : children}</div>
            {(showFooter && !error) && (
              <ModalActions
                showCancel={showCancel}
                actions={actions}
                classes={getFooterClasses(classes)}
                onClose={handleCloseModal}
              />
            )}
          </div>
        </SpinnerOverlay>
      </div>
    </MuiModal>
  );
};

export default memo(Modal);
